import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { Stepper, CopyButton, Badge, ActionIcon, Tooltip } from "@mantine/core";
import {
  Loader2Icon,
  CheckCircleIcon,
  XCircleIcon,
  CopyIcon,
  CheckIcon,
  RefreshCwIcon,
  TrashIcon,
  AlertCircleIcon,
} from "lucide-react";

import Message from "../../../../../i18n/Message";
import label from "../../../../../i18n/label";
import backend from "../../../../../backend/backend";
import Notify from "../../../../shared/Notify";
import { updateCurrentProject } from "../../../store/builderActionCreators";

import "./CustomDomainWizard.scss";

const CustomDomainWizard = ({ project }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const [active, setActive] = useState(0);
  const [domain, setDomain] = useState("");
  const [domainError, setDomainError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Initialize from project
  useEffect(() => {
    if (project?.customDomain) {
      setDomain(project.customDomain);
      // If already verified, go to step 3
      if (project.customDomainVerified) {
        setActive(2);
      } else {
        // If domain is set but not verified, go to step 2
        setActive(1);
      }
    } else {
      setDomain("");
      setActive(0);
    }
  }, [project?.customDomain, project?.customDomainVerified]);

  // Clear error when domain changes
  useEffect(() => {
    setDomainError("");
  }, [domain]);

  const isApexDomain = (d) => {
    if (!d) return false;
    const dotCount = (d.match(/\./g) || []).length;
    return dotCount === 1;
  };

  const validateDomain = (value) => {
    if (!value || value.length < 4) {
      return label(intl, "custom_domain_too_short", "Domain name is too short");
    }
    const domainPattern = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    if (!domainPattern.test(value)) {
      return label(intl, "custom_domain_invalid_format", "Invalid domain format");
    }
    return null;
  };

  const handleDomainSubmit = async (e) => {
    e.preventDefault();

    const trimmedDomain = domain.trim().toLowerCase();
    const error = validateDomain(trimmedDomain);
    if (error) {
      setDomainError(error);
      return;
    }

    setLoading(true);
    setDomainError("");

    try {
      const updatedProject = await backend.updateBuilderProject(workspaceId, project.uid, {
        customDomain: trimmedDomain,
      });

      dispatch(updateCurrentProject(updatedProject));
      setActive(1);
      Notify.success(label(intl, "custom_domain_saved", "Custom domain saved"));
    } catch (err) {
      if (err.json) {
        const errorJson = await err.json();
        const errorCode = errorJson?.error_code || errorJson?.message;
        setDomainError(
          errorCode
            ? label(intl, errorCode, errorCode)
            : label(intl, "error_saving_domain", "Failed to save custom domain")
        );
      } else {
        setDomainError(label(intl, "error_saving_domain", "Failed to save custom domain"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckDns = async () => {
    setChecking(true);

    try {
      const result = await backend.checkCustomDomainDns(workspaceId, project.uid);

      // Refresh project data
      const updatedProject = await backend.getBuilderProject(workspaceId, project.uid);
      dispatch(updateCurrentProject(updatedProject));

      if (result.verified) {
        setActive(2);
        Notify.success(label(intl, "custom_domain_dns_verified", "DNS verified successfully!"));
      } else {
        Notify.info(label(intl, "custom_domain_dns_not_verified", "DNS not configured yet"));
      }
    } catch (err) {
      Notify.error(label(intl, "error_checking_dns", "Failed to check DNS"));
    } finally {
      setChecking(false);
    }
  };

  const handleRemoveDomain = async () => {
    if (!window.confirm(label(intl, "custom_domain_remove_confirm", "Are you sure you want to remove this custom domain?"))) {
      return;
    }

    setRemoving(true);

    try {
      await backend.removeCustomDomain(workspaceId, project.uid);

      // Refresh project data
      const updatedProject = await backend.getBuilderProject(workspaceId, project.uid);
      dispatch(updateCurrentProject(updatedProject));

      setDomain("");
      setActive(0);
      Notify.success(label(intl, "custom_domain_removed", "Custom domain removed"));
    } catch (err) {
      Notify.error(label(intl, "error_removing_domain", "Failed to remove custom domain"));
    } finally {
      setRemoving(false);
    }
  };

  const renderStep1 = () => (
    <div className="wizard_step step_enter_domain">
      <form onSubmit={handleDomainSubmit}>
        <div className="form_group">
          <label htmlFor="customDomain">
            <Message id="custom_domain_enter_label" defaultMessage="Enter your domain" />
          </label>
          <input
            id="customDomain"
            type="text"
            className={`form-control ${domainError ? "is-invalid" : ""}`}
            value={domain}
            onChange={(e) => setDomain(e.target.value.toLowerCase())}
            placeholder={label(intl, "custom_domain_placeholder", "app.example.com")}
            disabled={loading || !!project?.customDomain}
          />
          {domainError && <div className="error_message">{domainError}</div>}
          <div className="form_hint">
            <Message
              id="custom_domain_enter_hint"
              defaultMessage="Enter a subdomain (e.g., app.example.com) or apex domain (e.g., example.com)"
            />
          </div>
        </div>

        {!project?.customDomain && (
          <div className="form_actions">
            <button type="submit" className="btn bg-gradient-dark" disabled={loading}>
              {loading && <Loader2Icon size={16} className="spinning" />}
              <Message id="custom_domain_continue" defaultMessage="Continue" />
            </button>
          </div>
        )}
      </form>
    </div>
  );

  const renderStep2 = () => {
    const isApex = isApexDomain(project?.customDomain);
    const targetValue = isApex ? project?.staticIp : project?.cnameTarget;
    const recordType = isApex ? "A" : "CNAME";

    return (
      <div className="wizard_step step_configure_dns">
        <div className="dns_instructions">
          <p>
            {isApex ? (
              <Message
                id="custom_domain_apex_instructions"
                defaultMessage="Add an A record pointing to:"
              />
            ) : (
              <Message
                id="custom_domain_cname_instructions"
                defaultMessage="Add a CNAME record pointing to:"
              />
            )}
          </p>

          <div className="dns_record_box">
            <div className="record_row">
              <span className="record_label">
                <Message id="custom_domain_record_type" defaultMessage="Type" />
              </span>
              <span className="record_value">{recordType}</span>
            </div>
            <div className="record_row">
              <span className="record_label">
                <Message id="custom_domain_record_name" defaultMessage="Name" />
              </span>
              <span className="record_value">
                {isApex ? "@" : project?.customDomain?.split(".")[0] || "app"}
              </span>
            </div>
            <div className="record_row">
              <span className="record_label">
                <Message id="custom_domain_record_value" defaultMessage="Value" />
              </span>
              <span className="record_value target_value">
                {targetValue}
                <CopyButton value={targetValue || ""}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? "Copied" : "Copy"} withArrow position="right">
                      <ActionIcon variant="subtle" onClick={copy} size="sm">
                        {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </span>
            </div>
          </div>

          <div className="dns_hint">
            <AlertCircleIcon size={16} />
            <Message
              id="custom_domain_ttl_hint"
              defaultMessage="Set TTL to 300 or lower for faster propagation"
            />
          </div>
        </div>

        <div className="form_actions">
          <button
            type="button"
            className="btn bg-gradient-dark"
            onClick={handleCheckDns}
            disabled={checking}
          >
            {checking && <Loader2Icon size={16} className="spinning" />}
            <RefreshCwIcon size={16} />
            <Message id="custom_domain_check_dns" defaultMessage="Check DNS" />
          </button>

          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={handleRemoveDomain}
            disabled={removing}
          >
            {removing && <Loader2Icon size={16} className="spinning" />}
            <TrashIcon size={16} />
            <Message id="custom_domain_remove" defaultMessage="Remove" />
          </button>
        </div>
      </div>
    );
  };

  const renderStep3 = () => (
    <div className="wizard_step step_verified">
      <div className="verified_status">
        <div className="status_row">
          <span className="status_label">
            <Message id="custom_domain_status_dns" defaultMessage="DNS Status" />
          </span>
          <Badge
            color={project?.customDomainVerified ? "green" : "yellow"}
            variant="light"
            leftSection={
              project?.customDomainVerified ? (
                <CheckCircleIcon size={14} />
              ) : (
                <XCircleIcon size={14} />
              )
            }
          >
            {project?.customDomainVerified
              ? label(intl, "custom_domain_verified", "Verified")
              : label(intl, "custom_domain_not_verified", "Not verified")}
          </Badge>
        </div>

        <div className="status_row">
          <span className="status_label">
            <Message id="custom_domain_status_ssl" defaultMessage="SSL Certificate" />
          </span>
          <Badge
            color={project?.customDomainSslProvisioned ? "green" : "yellow"}
            variant="light"
            leftSection={
              project?.customDomainSslProvisioned ? (
                <CheckCircleIcon size={14} />
              ) : (
                <Loader2Icon size={14} className="spinning" />
              )
            }
          >
            {project?.customDomainSslProvisioned
              ? label(intl, "custom_domain_ssl_ready", "Active")
              : label(intl, "custom_domain_ssl_provisioning", "Provisioning...")}
          </Badge>
        </div>

        <div className="status_row">
          <span className="status_label">
            <Message id="custom_domain_url" defaultMessage="Your URL" />
          </span>
          <a
            href={`https://${project?.customDomain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="domain_link"
          >
            https://{project?.customDomain}
          </a>
        </div>
      </div>

      <div className="form_actions">
        <button
          type="button"
          className="btn bg-gradient-dark"
          onClick={handleCheckDns}
          disabled={checking}
        >
          {checking && <Loader2Icon size={16} className="spinning" />}
          <RefreshCwIcon size={16} />
          <Message id="custom_domain_refresh_status" defaultMessage="Refresh Status" />
        </button>

        <button
          type="button"
          className="btn btn-outline-danger"
          onClick={handleRemoveDomain}
          disabled={removing}
        >
          {removing && <Loader2Icon size={16} className="spinning" />}
          <TrashIcon size={16} />
          <Message id="custom_domain_remove" defaultMessage="Remove" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="custom_domain_wizard">
      <Stepper active={active} onStepClick={setActive} size="sm" allowNextStepsSelect={false}>
        <Stepper.Step
          label={label(intl, "custom_domain_step_enter", "Enter Domain")}
          description={label(intl, "custom_domain_step_enter_desc", "Your custom domain")}
        >
          {renderStep1()}
        </Stepper.Step>

        <Stepper.Step
          label={label(intl, "custom_domain_step_configure", "Configure DNS")}
          description={label(intl, "custom_domain_step_configure_desc", "Add DNS records")}
        >
          {renderStep2()}
        </Stepper.Step>

        <Stepper.Step
          label={label(intl, "custom_domain_step_verify", "Verify")}
          description={label(intl, "custom_domain_step_verify_desc", "Confirm setup")}
        >
          {renderStep3()}
        </Stepper.Step>
      </Stepper>
    </div>
  );
};

export default CustomDomainWizard;
